import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { visit } from "unist-util-visit";
import CodeBlock from "@/components/CodeBlock";

// ──────────────────────────────────────────────────────────────────────────
// SANITIZED MARKDOWN RENDERING (LOT 6a)
//
// Security model:
//  - No raw HTML is ever parsed (rehype-raw is NOT used; skipHtml drops any
//    html nodes), so <script>, <iframe>, <img onerror=...> and on* handlers
//    cannot reach the DOM.
//  - rehype-sanitize enforces a strict ALLOWLIST built below — anything not
//    explicitly permitted (tag or attribute) is stripped.
//  - Output is a React element tree; we never use dangerouslySetInnerHTML.
//  - Links are restricted to http/https/mailto (blocks javascript:/data:/
//    vbscript:) and always rendered with rel="noopener noreferrer nofollow".
//  - Markdown images are disallowed (no <img>): closes the SSRF / tracking
//    vector of the browser auto-fetching arbitrary URLs. Real images come
//    through the attachment pipeline instead.
// ──────────────────────────────────────────────────────────────────────────

const SCHEMA = {
  ...defaultSchema,
  // Explicit element allowlist (note: no img/script/style/iframe/input/svg…).
  tagNames: [
    "p", "br", "strong", "em", "del", "a", "code", "pre",
    "ul", "ol", "li", "blockquote", "h1", "h2", "h3", "hr",
    "table", "thead", "tbody", "tr", "th", "td", "span",
  ],
  attributes: {
    a: ["href", "title"],
    code: ["className"],                 // language-xxx on fenced code
    span: [["className", "mention"]],     // only our mention marker class
    th: ["align"],
    td: ["align"],
  },
  // Link protocol allowlist — javascript:, data:, vbscript: are dropped.
  protocols: {
    ...defaultSchema.protocols,
    href: ["http", "https", "mailto"],
  },
  // Drop anything clobber-prone; keep defaults otherwise.
  clobberPrefix: "user-content-",
};

// remark plugin: turn "@name" in plain text into a <span class="mention"> node.
// Skips link/code contexts so we don't rewrite URLs or inline code.
function remarkMentions() {
  const re = /@[\w-]+/g;
  return (tree) => {
    visit(tree, "text", (node, index, parent) => {
      if (!parent || index == null) return;
      if (parent.type === "link" || parent.type === "linkReference") return;
      const value = node.value;
      const out = [];
      let last = 0;
      let m;
      re.lastIndex = 0;
      while ((m = re.exec(value)) !== null) {
        if (m.index > last) out.push({ type: "text", value: value.slice(last, m.index) });
        out.push({
          type: "mention",
          data: { hName: "span", hProperties: { className: ["mention"] } },
          children: [{ type: "text", value: m[0] }],
        });
        last = m.index + m[0].length;
      }
      if (!out.length) return;
      if (last < value.length) out.push({ type: "text", value: value.slice(last) });
      parent.children.splice(index, 1, ...out);
      return index + out.length;  // continue past the inserted nodes
    });
  };
}

const COMPONENTS = {
  a: ({ href, children, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="text-brand underline underline-offset-2 hover:text-brand-2 break-words"
      {...props}
    >{children}</a>
  ),
  span: ({ className, children, ...props }) =>
    (className || "").includes("mention")
      ? <span className="bg-brand/15 text-brand px-1 rounded-sm">{children}</span>
      : <span className={className} {...props}>{children}</span>,
  // Fenced/multi-line code → the existing CodeBlock (copy button, language).
  // Inline code → a small styled <code>.
  code: ({ className, children, ...props }) => {
    const lang = /language-(\w+)/.exec(className || "")?.[1];
    const text = String(children ?? "").replace(/\n$/, "");
    if (lang || text.includes("\n")) {
      return <CodeBlock lang={lang || "text"} code={text} compact />;
    }
    return <code className="bg-fg/10 px-1 py-0.5 rounded-sm text-[0.85em] mono" {...props}>{children}</code>;
  },
  // CodeBlock renders its own <pre>; unwrap markdown's wrapper to avoid nesting.
  pre: ({ children }) => <>{children}</>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-line/30 pl-3 text-fg/70 italic">{children}</blockquote>,
  h1: ({ children }) => <h1 className="text-base font-bold mt-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold mt-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mt-1">{children}</h3>,
  hr: () => <hr className="border-line/10 my-2" />,
  table: ({ children }) => <table className="border-collapse text-xs my-1">{children}</table>,
  th: ({ children, ...p }) => <th className="border border-line/20 px-2 py-1 text-left font-semibold" {...p}>{children}</th>,
  td: ({ children, ...p }) => <td className="border border-line/10 px-2 py-1" {...p}>{children}</td>,
};

export default function Markdown({ children }) {
  return (
    <div className="md-body whitespace-normal break-words [&_p]:whitespace-pre-wrap [&>*:first-child]:mt-0">
      <ReactMarkdown
        skipHtml
        remarkPlugins={[remarkGfm, remarkBreaks, remarkMentions]}
        rehypePlugins={[[rehypeSanitize, SCHEMA]]}
        components={COMPONENTS}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
